import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';

// Public Pages
import Landing from './pages/public/Landing';
import Login from './pages/auth/Login';
import AdminLogin from './pages/auth/AdminLogin';
import SellerLogin from './pages/auth/SellerLogin';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import OTPVerification from './pages/auth/OTPVerification';
import ResetPassword from './pages/auth/ResetPassword';
import OrderForm from './pages/public/OrderForm';
import RefundForm from './pages/public/RefundForm';
import TrackOrder from './pages/public/TrackOrder';
import ActiveDeals from './pages/public/ActiveDeals';
import Guides from './pages/public/Guides';

// Buyer Pages
import BuyerDashboard from './pages/buyer/Dashboard';
import BuyerOrders from './pages/buyer/Orders';
import BuyerWallet from './pages/buyer/Wallet';
import BuyerSettings from './pages/buyer/Settings';
import BuyerNotifications from './pages/buyer/Notifications';

// Seller Pages
import SellerDashboard from './pages/seller/Dashboard';

// Mediator Pages
import MediatorDashboard from './pages/mediator/Dashboard';
import MediatorOrders from './pages/mediator/Orders';
import AvailableDeals from './pages/mediator/AvailableDeals';
import MyDeals from './pages/mediator/MyDeals';
import MediatorPayouts from './pages/mediator/Payouts';
import MediatorSettings from './pages/mediator/Settings';
import MediatorNotifications from './pages/mediator/Notifications';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminOrders from './pages/admin/Orders';
import AdminActionRequired from './pages/admin/ActionRequired';
import AdminPenalties from './pages/admin/Penalties';
import AdminUsers from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminPlatforms from './pages/admin/Platforms';
import AdminMediators from './pages/admin/Mediators';
import AdminPayouts from './pages/admin/Payouts';
import AdminNotifications from './pages/admin/Notifications';
import AdminSettings from './pages/admin/Settings';
import AdminSheets from './pages/admin/Sheets';
import CreatePlatform from './pages/admin/CreatePlatform';
import CreateProduct from './pages/admin/CreateProduct';
import CreateMediator from './pages/admin/CreateMediator';

import AdminDealRequests from './pages/admin/DealRequests';

// Error Pages
import NotFound from './pages/error/NotFound';
import ServerError from './pages/error/ServerError';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/order" element={<OrderForm />} />
            <Route path="/refund" element={<RefundForm />} />
            <Route path="/track" element={<TrackOrder />} />
            <Route path="/deals" element={<ActiveDeals />} />
            <Route path="/guides" element={<Guides />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/seller-login" element={<SellerLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-otp" element={<OTPVerification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Buyer Routes */}
            <Route path="/buyer" element={
              <ProtectedRoute requiredRole="buyer">
                <Layout sidebarVariant="default" />
              </ProtectedRoute>
            }>
              <Route index element={<BuyerDashboard />} />
              <Route path="orders" element={<BuyerOrders />} />
              <Route path="notifications" element={<BuyerNotifications />} />
              <Route path="wallet" element={<BuyerWallet />} />
              <Route path="settings" element={<BuyerSettings />} />
            </Route>

          {/* Seller Routes */}
          <Route path="/seller" element={
            <ProtectedRoute requiredRole="seller">
              <Layout sidebarVariant="default" />
            </ProtectedRoute>
          }>
            <Route index element={<SellerDashboard />} />
          </Route>
          
          {/* Mediator Routes */}
          <Route path="/mediator" element={
            <ProtectedRoute requiredRole="mediator">
              <Layout sidebarVariant="default" />
            </ProtectedRoute>
          }>
            <Route index element={<MediatorDashboard />} />
            <Route path="notifications" element={<MediatorNotifications />} />
            <Route path="orders" element={<MediatorOrders />} />
            <Route path="available-deals" element={<AvailableDeals />} />
            <Route path="my-deals" element={<MyDeals />} />
            <Route path="payouts" element={<MediatorPayouts />} />
            <Route path="settings" element={<MediatorSettings />} />
          </Route>
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <Layout sidebarVariant="admin" />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="action-required" element={<AdminActionRequired />} />
            <Route path="penalties" element={<AdminPenalties />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="platforms" element={<AdminPlatforms />} />
            <Route path="create-platform" element={<CreatePlatform />} />
            <Route path="create-product" element={<CreateProduct />} />
            <Route path="mediators" element={<AdminMediators />} />
            <Route path="create-mediator" element={<CreateMediator />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="sheets" element={<AdminSheets />} />
            <Route path="deal-requests" element={<AdminDealRequests />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          
          {/* Error Routes */}
          <Route path="/500" element={<ServerError />} />
          <Route path="*" element={<NotFound />} />
          
          {/* Redirect old routes */}
          <Route path="/buyer-login" element={<Navigate to="/login?role=buyer" replace />} />
          <Route path="/med-login" element={<Navigate to="/login?role=mediator" replace />} />
          <Route path="/admin-login" element={<Navigate to="/login?role=admin" replace />} />
          <Route path="/buyer-dash" element={<Navigate to="/buyer" replace />} />
          <Route path="/med-panel" element={<Navigate to="/mediator" replace />} />
          <Route path="/admin-panel" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
